import { useEffect, useRef, useState } from "react";
import { sendVoiceMessage } from "./api/transcriptionService";

// Audio Recording Hook
const useAudioRecording = () => {
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioFile, setAudioFile] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [transcribedText, setTranscribedText] = useState("");
  const [isTranscribing, setIsTranscribing] = useState(false);

  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);
  const audioRef = useRef(null);
  const streamRef = useRef(null);

  const startRecording = async () => {
    try {
      // Clear previous states before starting new recording
      setAudioFile(null);
      setTranscribedText("");
      setIsTranscribing(false);
      setRecordingTime(0);

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 44100,
        },
      });

      streamRef.current = stream;
      audioChunksRef.current = [];

      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: MediaRecorder.isTypeSupported("audio/webm")
          ? "audio/webm"
          : "audio/mp4",
      });

      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, {
          type: mediaRecorder.mimeType,
        });

        // Convert Blob to File
        const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
        const extension = mediaRecorder.mimeType.includes("webm")
          ? "webm"
          : "mp4";
        const fileName = `recording-${timestamp}.${extension}`;

        const audioFile = new File([audioBlob], fileName, {
          type: mediaRecorder.mimeType,
          lastModified: Date.now(),
        });

        setAudioFile(audioFile);

        // Automatically transcribe the audio after recording stops
        handleTranscription(audioFile);

        // Stop all tracks
        if (streamRef.current) {
          streamRef.current.getTracks().forEach((track) => track.stop());
        }
      };

      mediaRecorder.start(100); // Collect data every 100ms
      setIsRecording(true);
      setRecordingTime(0);

      // Start timer
      timerRef.current = setInterval(() => {
        setRecordingTime((prev) => prev + 1);
      }, 1000);
    } catch (error) {
      console.error("Error starting recording:", error);
      alert("Could not access microphone. Please check permissions.");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);

      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
  };

  const handleTranscription = async (audioFile) => {
    if (!audioFile) return;

    setIsTranscribing(true);
    try {
      const transcription = await sendVoiceMessage(audioFile);
      setTranscribedText(transcription);
    } catch (error) {
      console.error("Transcription failed:", error);
      setTranscribedText("Transcription failed. Please try again.");
    } finally {
      setIsTranscribing(false);
    }
  };

  const cancelRecording = () => {
    // Stop recording if it's active
    if (isRecording && mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }

    // Clear timer
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    // Stop audio stream
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
    }

    // Reset all states
    setAudioFile(null);
    setRecordingTime(0);
    setTranscribedText("");
    setIsTranscribing(false);
  };

  const playAudio = () => {
    if (audioFile && !isPlaying) {
      const audioUrl = URL.createObjectURL(audioFile);
      audioRef.current = new Audio(audioUrl);

      audioRef.current.onended = () => {
        setIsPlaying(false);
        URL.revokeObjectURL(audioUrl);
      };

      audioRef.current.play();
      setIsPlaying(true);
    }
  };

  const pauseAudio = () => {
    if (audioRef.current && isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  // Cleanup
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      if (audioRef.current) {
        audioRef.current.pause();
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
    };
  }, []);

  // Reset all audio states (useful for clearing everything)
  const resetAudio = () => {
    setAudioFile(null);
    setTranscribedText("");
    setIsTranscribing(false);
    setRecordingTime(0);
    setIsPlaying(false);
  };

  return {
    isRecording,
    recordingTime,
    audioFile,
    isPlaying,
    transcribedText,
    isTranscribing,
    startRecording,
    stopRecording,
    cancelRecording,
    playAudio,
    pauseAudio,
    formatTime,
    handleTranscription,
    resetAudio,
  };
};

export default useAudioRecording;
